import React, { useState, useEffect, useRef } from 'react';
import LocationAutocomplete from '../../LocationAutocomplete';
import styled from 'styled-components';
import { useCurrency } from '../../../contexts/CurrencyContext';

// Custom styled wrapper for our custom date input
const DatePickerContainer = styled.div.attrs({
    className: 'date-picker-container'
})`
  position: relative;
  width: 100%;
  
  .date-input {
    width: 100%;
    padding: 0.5rem 0.5rem 0.5rem 2.5rem;
    background-color: transparent;
    border: 1px solid white;
    border-radius: 0.5rem;
    color: white;
    font-size: 14px !important;
    font-weight: 300;
    outline: none;
    cursor: pointer;
    margin-bottom: 0;
  }
  
  .date-input:focus {
    border-color: #A961ED;
  }
  
  .date-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: white;
    pointer-events: none;
  }
`;

const Calendar = styled.div.attrs({
    className: 'calendar-dropdown'
})`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  width: 300px; /* Fixed width */
  min-width: 100%; /* Ensure it's at least as wide as the input */
  max-width: 400px; /* Maximum width */
  background-color: black;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #374151;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5);
  z-index: 50;
  
  .calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background-color: rgba(31, 41, 55, 0.5);
    border-bottom: 1px solid #374151;
  }
  
  .month-selector {
    display: flex;
    align-items: center;
    margin: 0 auto;
    cursor: pointer;
    padding: 5px 10px;
    border: 1px solid #4b5563;
    border-radius: 0.25rem;
    background-color: #1f2937;
    transition: background-color 0.2s;
  }
  
  .month-selector:hover {
    background-color: rgba(75, 85, 99, 0.5);
  }
  
  .month-year-text {
    color: #f0f4f8;
    font-weight: 400;
    font-size: 12px !important;
  }
  
  .month-year-view {
    padding: 20px;
    
    h4 {
      font-size: 11px;
      font-weight: 500;
      margin-bottom: 8px;
      color: #9ca3af;
    }
  }
  
  .selectors-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }

  .selector-column {
    width: 100%;
  }
  
  .month-dropdown {
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #4b5563;
    border-radius: 0.25rem;
    background-color: #1f2937;
    scrollbar-width: thin;
    scrollbar-color: #A961ED;
  }
  
  .month-dropdown::-webkit-scrollbar {
    width: 8px;
  }
  
  .month-dropdown::-webkit-scrollbar-track {
    background: #A961ED;
    border-radius: 0.25rem;
  }
  
  .month-dropdown::-webkit-scrollbar-thumb {
    background-color: #dda74f;
    border-radius: 0.25rem;
  }
  
  .month-item {
    text-align: center;
    padding: 10px;
    cursor: pointer;
    color: white;
    transition: background-color 0.2s;
    font-size: 14px;
  }
  
  .year-dropdown-container {
    width: 100%;
  }
  
  .year-dropdown {
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #4b5563;
    border-radius: 0.25rem;
    background-color: #1f2937;
    scrollbar-width: thin;
    scrollbar-color: #A961ED;
  }
  
  .year-dropdown::-webkit-scrollbar {
    width: 8px;
  }
  
  .year-dropdown::-webkit-scrollbar-track {
    background: #A961ED;
    border-radius: 0.25rem;
  }
  
  .year-dropdown::-webkit-scrollbar-thumb {
    background-color: #A961ED;
    border-radius: 0.25rem;
  }
  
  .year-item {
    text-align: center;
    padding: 10px;
    cursor: pointer;
    color: white;
    transition: background-color 0.2s;
    font-size: 14px;
  }
  
  .month-item:hover, .year-item:hover {
    background-color: rgba(221, 167, 79, 0);
  }
  
  .month-item.selected, .year-item.selected {
    background-color: #A961ED;
    color: #000;
    font-weight: 600;
  }
  
  .calendar-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 10px;
  }
  
  .day-label {
    text-align: center;
    font-weight: 500;
    padding: 8px 0;
    font-size: 12px;
    color: #9ca3af;
  }
  
  .day {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0;
    cursor: pointer;
    border-radius: 9999px;
    font-size: 14px;
    transition: background-color 0.2s;
    color: #f0f4f8;
  }
  
  .day:hover:not(.empty, .selected) {
    background-color: rgba(221, 167, 79, 0.2);
  }
  
  .day.selected {
    background-color: #dda74f;
    color: #000;
    font-weight: 600;
  }
  
  .day.empty {
    cursor: default;
    color: #4b5563;
  }
  
  .day.weekend {
    color: white;
  }
  
  .day.weekend.empty {
    color: #6b7280;
  }
  
  .nav-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    transition: background-color 0.2s;
    color: #f0f4f8;
  }
  
  .nav-button:hover {
    background-color: rgba(75, 85, 99, 0.3);
  }

  .clear-button {
    background-color: transparent;
    border: 1px solid #4b5563;
    padding: 5px 15px;
    border-radius: 0.25rem;
    margin: 10px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
    color: #f0f4f8;
  }
  
  .clear-button:hover {
    background-color: rgba(75, 85, 99, 0.3);
  }
  
  .days-row {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }
  
  .back-to-days {
    display: flex;
    align-items: center;
    padding: 5px;
    color: #f0f4f8;
    cursor: pointer;
    margin-bottom: 10px;
    width: fit-content;
    border-radius: 0.25rem;
    font-size: 12px;
  }
  
  .back-to-days:hover {
    background-color: rgba(75, 85, 99, 0.3);
  }
  
  .back-to-days svg {
    margin-right: 5px;
  }
`;

// Day labels with Sunday and Saturday as red
const dayLabels = [
    { day: 'Sun', weekend: true },
    { day: 'Mon', weekend: false },
    { day: 'Tue', weekend: false },
    { day: 'Wed', weekend: false },
    { day: 'Thu', weekend: false },
    { day: 'Fri', weekend: false },
    { day: 'Sat', weekend: true },
];

// Create array of months
const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const MomentStep = ({ formData, handleInputChange, handleLocationSelect, handleToggleCelestialElement, cityName, errors }) => {
    const { formatPrice } = useCurrency();
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(formData.date ? new Date(formData.date) : null);
    const [viewMode, setViewMode] = useState('days'); // 'days', 'months-years'
    const calendarRef = useRef(null);
    const yearListRef = useRef(null);

    // Format date as MM/DD/YYYY for display
    const formatDateForDisplay = (date) => {
        if (!date) return '';
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    // Format date as YYYY-MM-DD for input value
    const formatDateForInput = (date) => {
        if (!date) return '';
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };

    // Get days in month
    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    // Get day of week for first day of month (0 = Sunday, 6 = Saturday)
    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };

    // Generate years for dropdown (1950 to 2050)
    const generateYears = () => {
        const years = [];
        for (let i = 1950; i <= 2050; i++) {
            years.push(i);
        }
        return years;
    };

    // Handle view change
    const handleViewChange = () => {
        setViewMode(viewMode === 'days' ? 'months-years' : 'days');
    };

    // Handle month selection in month view
    const handleMonthSelect = (monthIndex) => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(monthIndex);
            return newDate;
        });
        setViewMode('days');
    };

    // Handle year selection in year view
    const handleYearSelect = (year) => {
        // Remember the current scroll position
        const currentScrollPosition = yearListRef.current ? yearListRef.current.scrollTop : 0;

        // Update the current date
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setFullYear(year);
            return newDate;
        });

        // Restore the scroll position after state update
        // We need to use setTimeout to ensure this happens after React has updated the DOM
        setTimeout(() => {
            if (yearListRef.current) {
                yearListRef.current.scrollTop = currentScrollPosition;
            }
        }, 0);
    };

    // Handle previous month
    const handlePrevMonth = () => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(newDate.getMonth() - 1);
            return newDate;
        });
    };

    // Handle next month
    const handleNextMonth = () => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(newDate.getMonth() + 1);
            return newDate;
        });
    };

    // Handle day selection
    const handleDaySelect = (day) => {
        const newDate = new Date(currentDate);
        newDate.setDate(day);

        // Update selected date
        setSelectedDate(newDate);

        // Update form data
        const formattedDate = formatDateForInput(newDate);
        const event = {
            target: {
                name: 'date',
                value: formattedDate
            }
        };
        handleInputChange(event);

        // Close calendar
        setShowCalendar(false);
    };

    // Handle clear date
    const handleClearDate = () => {
        setSelectedDate(null);

        // Update form data
        const event = {
            target: {
                name: 'date',
                value: ''
            }
        };
        handleInputChange(event);

        // Close calendar
        setShowCalendar(false);
    };

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (calendarRef.current && !calendarRef.current.contains(e.target)) {
                setShowCalendar(false);
                // Reset to days view when calendar is closed
                setViewMode('days');
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    // Update selected date when formData.date changes
    useEffect(() => {
        if (formData.date) {
            setSelectedDate(new Date(formData.date));
        }
    }, [formData.date]);

    // Scroll to current year when the dropdown is shown
    useEffect(() => {
        // Only scroll when first switching to the months-years view
        if (viewMode === 'months-years' && yearListRef.current) {
            const years = generateYears();
            const currentYear = currentDate.getFullYear();
            const currentYearIndex = years.indexOf(currentYear);
            const scrollPosition = currentYearIndex * 40; // Approximate height of each year item
            yearListRef.current.scrollTop = scrollPosition - 80; // Center the current year in the dropdown
        }
    }, [viewMode]); // Only depend on viewMode, not currentDate

    // Generate calendar days
    const renderCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDayOfMonth = getFirstDayOfMonth(year, month);

        // Previous month days
        const prevMonthDays = [];
        if (firstDayOfMonth > 0) {
            const prevMonth = month === 0 ? 11 : month - 1;
            const prevMonthYear = month === 0 ? year - 1 : year;
            const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);

            for (let i = firstDayOfMonth - 1; i >= 0; i--) {
                prevMonthDays.push(
                    <div
                        key={`prev-${daysInPrevMonth - i}`}
                        className={`day empty ${(firstDayOfMonth - 1 - i) % 7 === 0 || (firstDayOfMonth - 1 - i) % 7 === 6 ? 'weekend' : ''}`}
                    >
                        {daysInPrevMonth - i}
                    </div>
                );
            }
        }

        // Current month days
        const currentMonthDays = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayOfWeek = date.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            currentMonthDays.push(
                <div
                    key={`current-${day}`}
                    className={`day ${selectedDate && day === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear() ? 'selected' : ''} ${isWeekend ? 'weekend' : ''}`}
                    onClick={() => handleDaySelect(day)}
                >
                    {day}
                </div>
            );
        }

        // Calculate if we need 6 rows or 5 rows
        const totalDaysShown = prevMonthDays.length + daysInMonth;
        const needsSixthRow = totalDaysShown > 35; // 5 rows = 35 days

        // Next month days - only fill to the end of the 5th or 6th row as needed
        const nextMonthDays = [];
        const totalSlotsNeeded = needsSixthRow ? 42 : 35; // 6 rows = 42, 5 rows = 35
        const remainingDays = totalSlotsNeeded - totalDaysShown;

        for (let day = 1; day <= remainingDays; day++) {
            const dayOfWeek = (firstDayOfMonth + daysInMonth + day - 1) % 7;
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            nextMonthDays.push(
                <div
                    key={`next-${day}`}
                    className={`day empty ${isWeekend ? 'weekend' : ''}`}
                >
                    {day}
                </div>
            );
        }

        return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
    };

    // Render month-year view
    const renderMonthYearView = () => {
        const years = generateYears();
        const currentYear = currentDate.getFullYear();

        return (
            <div className="month-year-view">
                <div className="back-to-days" onClick={handleViewChange}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Back to calendar
                </div>

                <div className="selectors-grid">
                    <div className="selector-column">
                        <h4>Select Month</h4>
                        <div className="month-dropdown">
                            {months.map((month, index) => (
                                <div
                                    key={month}
                                    className={`month-item ${currentDate.getMonth() === index ? 'selected' : ''}`}
                                    onClick={() => handleMonthSelect(index)}
                                >
                                    {month}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="selector-column">
                        <h4>Select Year</h4>
                        <div className="year-dropdown" ref={yearListRef}>
                            {years.map(year => (
                                <div
                                    key={year}
                                    className={`year-item ${currentDate.getFullYear() === year ? 'selected' : ''}`}
                                    onClick={() => handleYearSelect(year)}
                                >
                                    {year}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4 md:space-y-6 -mt-4">
            <div>
                <h3 className="text-[31px] font-light mb-4">Place of Your Special Moment</h3>
                <p className="mb-8 text-gray-300 text-md">
                    Anywhere in the world — from your hometown to a dream destination.
                </p>
            </div>

            <div className="grid !grid-cols-2 gap-4">
                <div className="form-group">
                    <label className="block mb-1 sm:mb-2 font-medium text-[14px]">
                        Date <span className="text-red-400">*</span>
                    </label>
                    <DatePickerContainer ref={calendarRef}>
                        <input
                            type="text"
                            className={`date-input ${!formData.date ? 'border-red-400' : ''}`}
                            placeholder="Select date"
                            value={selectedDate ? formatDateForDisplay(selectedDate) : ''}
                            onClick={() => setShowCalendar(!showCalendar)}
                            readOnly
                        />
                        <span className="date-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </span>

                        {showCalendar && (
                            <Calendar>
                                {viewMode === 'days' ? (
                                    <>
                                        <div className="calendar-header">
                                            <button className="nav-button" onClick={handlePrevMonth}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="15 18 9 12 15 6"></polyline>
                                                </svg>
                                            </button>

                                            <div className="month-selector" onClick={handleViewChange}>
                                                <span className="month-year-text">
                                                    {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                                                </span>
                                            </div>

                                            <button className="nav-button" onClick={handleNextMonth}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="9 18 15 12 9 6"></polyline>
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="calendar-days">
                                            {/* Day labels (Sun, Mon, etc.) */}
                                            {dayLabels.map(({ day, weekend }) => (
                                                <div key={day} className={`day-label ${weekend ? 'weekend' : ''}`}>{day}</div>
                                            ))}

                                            {/* Calendar days */}
                                            {renderCalendarDays()}
                                        </div>

                                        <button className="clear-button" onClick={handleClearDate}>
                                            Clear
                                        </button>
                                    </>
                                ) : (
                                    renderMonthYearView()
                                )}
                            </Calendar>
                        )}
                    </DatePickerContainer>
                    {errors.date && <p className="text-error text-xs mt-1">{errors.date}</p>}
                </div>

                <div className="form-group">
                    <label className="block mb-1 sm:mb-2 font-medium text-[14px]">
                        City Location <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                        <LocationAutocomplete
                            onSelect={handleLocationSelect}
                            initialValue={cityName}
                            className={!formData.location ? 'border-red-400' : ''}
                        />
                    </div>
                    {errors.location && <p className="text-error text-xs mt-1">{errors.location}</p>}
                </div>
            </div>

            <div className="border-t border-dotted border-[#A0A0A0] !mt-10 !mb-6"></div>

            <div>
                <h3 className="text-[31px] font-light mb-3">Celestial Elements</h3>
                <p className="text-md text-white mb-6">Customize what appears on your star map</p>

                <div className="space-y-3">
                    {[
                        { id: 'showStars', label: 'Stars', icon: '✦', price: 4.00 },
                        { id: 'showConstellations', label: 'Constellation lines', icon: '⋆', price: 5.00 },
                        { id: 'showConstellationNames', label: 'Constellation names', icon: 'Aa', price: 5.00 },
                        { id: 'showGrid', label: 'Coordination Grid', icon: '⊞', price: 8.00 },
                        { id: 'showDeepSkyObjects', label: 'Deep Sky Objects', icon: '◎', price: 13.00 },
                        { id: 'showEcliptic', label: 'Ecliptic line', icon: '⌒', price: 17.50 }
                    ].map((option) => (
                        <div key={option.id} className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="relative flex items-center">
                                    <input
                                        id={option.id}
                                        type="checkbox"
                                        checked={formData[option.id]}
                                        onChange={() => handleToggleCelestialElement(option.id)}
                                        className="opacity-0 absolute h-[20px] w-[20px] cursor-pointer z-10"
                                    />
                                    <div
                                        className="h-[20px] w-[20px] rounded-md border border-white bg-black flex items-center justify-center cursor-pointer"
                                        onClick={() => handleToggleCelestialElement(option.id)}
                                    >
                                        {formData[option.id] && (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <label htmlFor={option.id} className="ml-3 text-md cursor-pointer !mt-[6px]">
                                    <span className="font-medium text-white mr-2">{option.label}</span>
                                </label>
                            </div>
                            <span className="text-md font-medium">{formatPrice(option.price)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MomentStep; 